import React from "react";

interface FieldOption {
  value: string | number;
  label: string;
}

interface FieldProps {
  label: string;
  name: string;
  value: string | number | Date;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  options?: FieldOption[];
}

export default function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled = false,
  required = false,
  options = [],
}: FieldProps) {
  // Manejo especial para formatear objetos Date a string YYYY-MM-DD para input type="date"
  const getInputValue = () => {
    if (type === "date" && value) {
      // Manejar tanto objeto Date como string ISO o YYYY-MM-DD
      const date = value instanceof Date ? value : new Date(value);
      if (!isNaN(date.getTime())) {
        // Aseguramos formato YYYY-MM-DD para el input HTML
        return date.toISOString().split("T")[0];
      }
    }
    if (value === null || value === undefined) return "";
    return value.toString();
  };

  const inputClasses = `
    w-full bg-white/80 border border-black/5 rounded-xl py-3 px-4 text-base font-medium text-foreground 
    focus:outline-none focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all shadow-sm 
    disabled:opacity-50 disabled:cursor-not-allowed
    ${type === "date" ? "cursor-pointer [appearance:none] [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden scheme-light dark:scheme-dark" : ""}
    ${type === "select" ? "cursor-pointer appearance-none pr-10" : ""}
  `;

  return (
    <div className="space-y-2 group">
      <label className="text-sm font-bold text-foreground/60 ml-1 uppercase tracking-wider group-focus-within:text-accent transition-colors">
        {label}
      </label>
      <div className="relative">
        {type === "select" ? (
          <select
            name={name}
            value={value?.toString() || ""}
            onChange={onChange}
            disabled={disabled}
            required={required}
            className={inputClasses}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            name={name}
            value={getInputValue()}
            onChange={onChange}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            onClick={(e) => type === "date" && e.currentTarget.showPicker?.()}
            className={inputClasses}
            style={
              type === "date"
                ? {
                    colorScheme: "light",
                  }
                : {}
            }
          />
        )}

        {/* Icono para select o date */}
        {(type === "date" || type === "select") && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40 group-focus-within:opacity-100 group-hover:opacity-70 transition-opacity">
            {type === "date" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-accent"
              >
                <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                <line x1="16" x2="16" y1="2" y2="6" />
                <line x1="8" x2="8" y1="2" y2="6" />
                <line x1="3" x2="21" y1="10" y2="10" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-accent"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
